import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import {
  currentTeam,
  currentVideoSrc,
  selectedBBBVideo,
  translationType,
} from '../../store/translation/selectors';

import {
  videoPanelStyle,
  videoInnerContainerStyle,
  metaInnerContainerStyle,
  isMobileWidthVideoPanel,
} from '../../store/screen/panelSelectors';

import fade from '../../styles/transitions/fade.module.css';
import styles from './styles.module.css';
import { RootState } from '../../store';
import ActionBar from './ActionBar';
import MetadataBar from './MetadataBar';
import CopyrightBar from './CopyrightBar';
import Arrow from './Arrow';
import MainVideoPlayer from '../MainVideoPlayer';
import LoadingIndicator from '../LoadingIndicator';
import { isMobile } from '../../store/screen/selectors';
import BBBResourceMain from '../MainVideoPlayer/BBBResourcesLayouts/BBBResourceMain';
import { getBBBResources } from '../../utils/api/getTranslation';
import { RecordItem } from '../MainVideoPlayer/BBBResourcesLayouts/types';
import ComingSoon from './ComingSoon';
import { LoadingState } from '../../types/loadingState';

const VideoPanel = (): JSX.Element => {
  const [bbbResources, setBbbResources] = useState<RecordItem[]>([]);
  const [showBBBResourceMain, setShowBBBResourceMain] = useState(false);

  const { darkMode } = useSelector((state: RootState) => state.settings);
  const { panelPosition } = useSelector((state: RootState) => state.textPanelSettings);
  const innerContainerStyle = useSelector(videoInnerContainerStyle);
  const metaContainerStyle = useSelector(metaInnerContainerStyle);
  const outerContainerStyle = useSelector(videoPanelStyle);
  const videoSrc = useSelector(currentVideoSrc);
  const isMobileDevice = useSelector(isMobile);
  const isMobileVideoPanel = useSelector(isMobileWidthVideoPanel);
  const selectedVideo = useSelector(selectedBBBVideo);
  const team = useSelector(currentTeam);
  const type = useSelector(translationType);
  const { bookOrSlug } = useSelector((state: RootState) => state.params);
  const { loadingState } = useSelector((state: RootState) => state.timestamps);

  // Function to get current video time
  const getCurrentVideoTime = (): number => {
    const mainVideoPlayer = document.querySelector(
      '#mainVideoPlayerContainer video'
    ) as HTMLVideoElement;
    if (mainVideoPlayer) {
      return mainVideoPlayer.currentTime;
    }
    return 0;
  };

  // Function to filter BBB resources based on current time
  const getFilteredBBBResources = (): RecordItem[] => {
    if (!bbbResources || bbbResources.length === 0) return [];

    const currentTime = getCurrentVideoTime();

    return bbbResources.filter(
      resource =>
        resource.startTime > 0 &&
        resource.endTime > 0 &&
        currentTime >= resource.startTime &&
        currentTime <= resource.endTime
    );
  };

  // Get filtered BBB resources
  const filteredBBBResources = getFilteredBBBResources();

  useEffect(() => {
    if (selectedVideo && selectedVideo.bbb_id) {
      getBBBResources(selectedVideo.bbb_id).then(records => {
        setBbbResources(records);
      });
    }
  }, [selectedVideo]);

  const addPadding = !isMobileDevice && isMobileVideoPanel && panelPosition === 'left';
  const innerContainer = `${styles.innerContainer} ${darkMode && styles.innerContainerDarkMode}`;
  const outerContainer = `${styles.outerContainer} ${
    addPadding && styles.outerContainerMobileWithTab
  } ${isMobileDevice && styles.outerContainerMobile}`;

  // Show panel if video exists, or if book is selected but no video (show coming soon)
  const hasVideo = videoSrc !== undefined;
  const isBookSelected = bookOrSlug !== undefined;
  const isBBBTranslation = type === 'BBB';
  const isLoading = loadingState === LoadingState.loading;
  const shouldShowComingSoon = isBookSelected && !hasVideo && isBBBTranslation && !isLoading;
  const show = hasVideo || shouldShowComingSoon;
  const loadingIndicatorImg = team?.icon_url;

  const openBBBResourceMain = () => {
    setShowBBBResourceMain(true);
    // Pause the main video player when BBB resource layout is opened
    const mainVideoPlayer = document.querySelector(
      '#mainVideoPlayerContainer video'
    ) as HTMLVideoElement;
    if (mainVideoPlayer) {
      mainVideoPlayer.pause();
    }
  };

  const closeBBBResourceMain = () => {
    setShowBBBResourceMain(false);
  };

  useEffect(() => {
    setShowBBBResourceMain(false);
  }, [videoSrc]);

  return (
    <CSSTransition in={show} classNames={fade} timeout={300} mountOnEnter unmountOnExit>
      <div className={outerContainer} style={outerContainerStyle}>
        {isMobileDevice && (
          <CSSTransition
            in={showBBBResourceMain}
            classNames={fade}
            timeout={300}
            mountOnEnter
            unmountOnExit
          >
            <div
              className={`${styles.bbbResourceContainer} ${
                showBBBResourceMain
                  ? styles.bbbResourceContainerShow
                  : styles.bbbResourceContainerHide
              }`}
            >
              <BBBResourceMain
                closeBBBResourceMain={closeBBBResourceMain}
                bbbResources={filteredBBBResources}
                isMobileDevice={isMobileDevice}
                isDarkMode={darkMode}
              />
            </div>
          </CSSTransition>
        )}
        <div className={styles.mainContainer}>
          <div className={innerContainer} style={innerContainerStyle}>
            {shouldShowComingSoon ? (
              <ComingSoon />
            ) : (
              <>
                <div className={styles.arrowsContainer}>
                  <Arrow direction="prev" />
                  <Arrow direction="next" />
                </div>
                <LoadingIndicator imgUrl={loadingIndicatorImg} isMobile={isMobileDevice} />
                <div style={{ position: 'relative' }}>
                  <div>
                    <MainVideoPlayer open={openBBBResourceMain} bbbResources={bbbResources} />
                  </div>
                  {!isMobileDevice && (
                    <CSSTransition
                      in={showBBBResourceMain}
                      classNames={fade}
                      timeout={300}
                      mountOnEnter
                      unmountOnExit
                    >
                      <div
                        className={`${styles.bbbResourceContainer} ${
                          showBBBResourceMain
                            ? styles.bbbResourceContainerShow
                            : styles.bbbResourceContainerHide
                        }`}
                      >
                        <BBBResourceMain
                          closeBBBResourceMain={closeBBBResourceMain}
                          bbbResources={filteredBBBResources}
                          isMobileDevice={isMobileDevice}
                          isDarkMode={darkMode}
                        />
                      </div>
                    </CSSTransition>
                  )}
                </div>
              </>
            )}
          </div>
          {!shouldShowComingSoon && (
            <div className={innerContainer} style={metaContainerStyle}>
              <ActionBar />
              <MetadataBar />
              <CopyrightBar />
            </div>
          )}
        </div>
      </div>
    </CSSTransition>
  );
};

export default VideoPanel;
